'use client'

import React, { forwardRef, useState } from 'react'

import { Box, chakra } from '@chakra-ui/react'

import { Tooltip } from '../Tooltip'
import { ColorScheme, tagStyles } from './Tag.styles'

export type { ColorScheme }

type TagBaseProps = {
  isArchived?: boolean
  /** Tooltip text shown on hover for archived tags. Defaults to "This label has been disabled" */
  archivedLabel?: string
  /** When true, suppresses the archived tooltip */
  disableTooltip?: boolean
  value: string
  className?: string
  maxWidth?: string | number
  colorScheme?: ColorScheme
}

export type TagProps = TagBaseProps &
  (
    | { editable: true; onRemove?: () => void }
    | { editable?: false; onRemove?: never }
  )

export const Tag = forwardRef<HTMLSpanElement, TagProps>(function Tag(
  {
    isArchived = false,
    archivedLabel = 'This label has been disabled',
    disableTooltip = false,
    value,
    className,
    maxWidth,
    colorScheme = 'dark',
    editable = false,
    onRemove,
  },
  ref
) {
  const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null)
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Box
      as='span'
      className={className}
      {...tagStyles(isArchived, maxWidth, colorScheme)}
      onMouseEnter={(e) => {
        if (editable) setIsHovered(true)
        if (isArchived) setAnchorEl(e.currentTarget as HTMLSpanElement)
      }}
      onMouseLeave={() => {
        if (editable) setIsHovered(false)
        if (isArchived) setAnchorEl(null)
      }}
      ref={ref}
    >
      {maxWidth !== undefined ? (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
      {editable && (
        <chakra.button
          alignItems='center'
          aria-label='Remove'
          background='none'
          border='none'
          color='inherit'
          cursor='pointer'
          display='inline-flex'
          justifyContent='center'
          maxWidth={isHovered ? '16px' : '0px'}
          ml={isHovered ? '6px' : '0px'}
          onClick={(e) => {
            e.stopPropagation()
            onRemove?.()
          }}
          opacity={isHovered ? 1 : 0}
          overflow='hidden'
          padding='0'
          transition='max-width 0.15s ease, opacity 0.15s ease, margin-left 0.15s ease'
          type='button'
        >
          <svg
            fill='none'
            height='8'
            viewBox='0 0 8 8'
            width='8'
            xmlns='http://www.w3.org/2000/svg'
          >
            <line
              stroke='currentColor'
              strokeLinecap='round'
              strokeWidth='1.5'
              x1='1'
              x2='7'
              y1='1'
              y2='7'
            />
            <line
              stroke='currentColor'
              strokeLinecap='round'
              strokeWidth='1.5'
              x1='7'
              x2='1'
              y1='1'
              y2='7'
            />
          </svg>
        </chakra.button>
      )}
      {isArchived && !disableTooltip && (
        <Tooltip
          anchorEl={anchorEl}
          colorScheme={colorScheme}
          label={archivedLabel}
        />
      )}
    </Box>
  )
})
